<svelte:options runes={false} />
<script>
  // Candles & Signals tab (U12b/KTD11): full strategy transparency — a candle chart with the strategy's signal
  // markers (▲ buy / ▼ sell / · hold) plus a raw per-tick debug table ({time, OHLC, each indicator, signal,
  // decision}). Historical series comes from the membership-gated U8c read; the LIVE half rides the host's
  // NAV-tick refetch (KTD7 hint-then-refetch) — the strategy's `lastBarTime` advancing on each authoritative
  // refetch is our live signal to re-pull the recent window, so the chart advances in realtime for a running
  // strategy without a dedicated signal-bar push. For ended/paused strategies the historical read alone.
  //
  // Honest gap states (KTD11): a captured signal row with no OHLCV candle still renders its marker (the chart
  // draws a hollow gap tick) — never silently dropped.
  import { onMount, onDestroy } from 'svelte';
  import api from '../../../lib/api.js';
  import { selectedOrgId } from '../../../lib/orgContext.js';
  import { navTickSeq } from '../../../lib/autotradeStore.js';
  import { parseSymbols } from '../../../lib/symbolUniverse.js';
  import CandleChart from '../../shared/charts/CandleChart.svelte';
  import SymbolUniverse from '../../shared/SymbolUniverse.svelte';

  export let strategy = null;
  export let status = null;
  export let isLive = false;

  // The strategy's traded symbols — ONE derivation shared with the switcher (parseSymbols dedupes across
  // delimiter/case drift), so the host's count and the strip's chips can never disagree. `symbols` keeps the
  // RAW tokens (request form, e.g. BTC-USD): the series fetch keys on them.
  $: entries = parseSymbols(strategy?.symbols);
  $: symbols = entries.map((e) => e.raw);
  $: displayOf = (raw) => entries.find((e) => e.raw === raw)?.display ?? raw;
  let selectedSymbol = '';
  $: if (symbols.length && !symbols.includes(selectedSymbol)) selectedSymbol = symbols[0];

  // `sym` is the RAW token from the strategy's list — the series fetch keys on it, so it is never re-derived.
  function selectSymbol(sym) {
    if (sym !== selectedSymbol) selectedSymbol = sym;
  }

  let series = [];
  let loading = false;
  let error = null;
  let inFlight = false; // coalesce overlapping refetches (NAV-tick hint + fallback poll racing)

  $: strategyId = strategy?.strategyId ?? null;
  $: isRunning = String(status) === 'Running';

  async function loadSeries() {
    if (!strategyId || !selectedSymbol) return;
    if (inFlight) return; // a refetch is already running — don't stack a second full-series pull on top
    inFlight = true;
    loading = true;
    error = null;
    const at = $selectedOrgId;
    const sym = selectedSymbol;
    try {
      const rows = await api.getStrategySignals(strategyId, sym, {}, at);
      // Drop a stale response (org/strategy/symbol changed mid-flight).
      if ($selectedOrgId !== at || strategy?.strategyId !== strategyId || selectedSymbol !== sym) return;
      series = Array.isArray(rows) ? rows : [];
    } catch (err) {
      const mapped = mapError(err);
      // A 404 means "no captured series" — clear to the empty state, don't leave a stale chart rendered as
      // current (mapError returns null for 404, which would otherwise keep the previous series on screen).
      if (mapped === null) series = [];
      error = mapped;
    } finally {
      loading = false;
      inFlight = false;
    }
  }

  function mapError(err) {
    const msg = String(err?.message ?? err);
    if (msg.includes('status: 404')) return null; // no captured series yet — show the empty state, not an error
    const idx = msg.indexOf('details: ');
    return idx >= 0 ? msg.slice(idx + 'details: '.length) : msg;
  }

  // Reload on strategy/symbol change. Clear the prior series first so switching symbol tabs shows the loading
  // skeleton for the NEW symbol rather than briefly lingering on the previous symbol's chart/table.
  let lastKey = '';
  $: {
    const key = `${strategyId}|${selectedSymbol}`;
    if (key !== lastKey && strategyId && selectedSymbol) {
      lastKey = key;
      series = [];
      loadSeries();
    }
  }

  // LIVE half: re-pull on each NAV-tick SSE hint (navTickSeq, bumped by the host's onNavTick) for a running
  // strategy — the KTD7 hint-then-refetch pattern, so the chart advances live without a dedicated signal-bar
  // push. Track the last-seen seq so we only refetch on an actual new tick, not on every reactive re-run.
  let lastSeq = 0;
  $: if (isRunning && $navTickSeq !== lastSeq) {
    lastSeq = $navTickSeq;
    loadSeries();
  }

  // Fallback liveness poll: the NAV-tick hint is best-effort (an SSE drop, a coalesced burst, or a slow-cadence
  // strategy can leave gaps), so a running strategy ALSO self-refreshes on a fixed interval — decoupled from the
  // push, mirroring the loop's own poll-decoupled-from-frequency design. This is what makes a freshly-mounted tab
  // for a slow (e.g. 1-hour) strategy fill in once its first bar is captured instead of sitting empty until the
  // next NAV push. `inFlight` in loadSeries() coalesces this with a concurrent NAV-hint refetch.
  const FALLBACK_POLL_MS = 30000;
  let pollTimer = null;
  $: if (isRunning && !pollTimer) {
    pollTimer = setInterval(loadSeries, FALLBACK_POLL_MS);
  } else if (!isRunning && pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  onDestroy(() => { if (pollTimer) clearInterval(pollTimer); });

  // The indicator columns present in the series (union of keys across points), for the debug table header.
  $: indicatorKeys = (() => {
    const set = new Set();
    for (const p of series) for (const k of Object.keys(p.indicators ?? {})) set.add(k);
    return [...set].sort();
  })();

  function signalLabel(s) {
    const v = Number(s);
    return v > 0 ? 'BUY' : v < 0 ? 'SELL' : 'HOLD';
  }
  function fmtNum(v) {
    return v == null ? '—' : Number(v).toLocaleString('en-US', { maximumFractionDigits: 4 });
  }
  function fmtTime(t) {
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  }

  onMount(loadSeries);
</script>

<div class="at-candles-tab">
  {#if symbols.length > 1}
    <!-- The symbol switcher (TASK-20260823): one chip-tab per traded symbol, collapsed to ONE row with the
         active symbol pinned first; "+N more" expands inline with a filter so any of a 400-name universe is two
         keystrokes away. Arrows move focus; Enter/Space/click select (manual activation — a fetch per arrow
         across 400 names would be waste). The active symbol's chart + table render below. -->
    <div class="at-symbol-switcher">
      <SymbolUniverse selectable selected={selectedSymbol} symbols={strategy?.symbols} variant="block"
        label="Symbols" on:select={(e) => selectSymbol(e.detail)} />
    </div>
  {/if}

  <div class="at-candles-toolbar">
    {#if symbols.length === 1}
      <span class="at-symbol-single">{entries[0].display}</span>
    {/if}
    {#if isRunning}
      <span class="at-live-badge" title="Updating live on each NAV tick">● live</span>
    {/if}
    <button type="button" class="btn btn-ghost btn-sm" on:click={loadSeries} disabled={loading}>Refresh</button>
  </div>

  {#if error}
    <div class="alert alert-danger" role="alert">{error}
      <button type="button" class="btn btn-ghost btn-sm" on:click={loadSeries}>Retry</button>
    </div>
  {:else if loading && series.length === 0}
    <div class="at-skeleton-rows"><div class="at-skeleton-row"></div></div>
  {:else if series.length === 0}
    <div class="empty-state">
      <p class="empty-state-description">
        No captured candles/signals for {selectedSymbol ? displayOf(selectedSymbol) : 'this symbol'} yet.
        {#if isRunning}They'll appear here as the strategy ticks.{/if}
      </p>
    </div>
  {:else}
    <CandleChart data={series} />

    <details class="at-debug-table">
      <summary>Raw per-tick signal data ({series.length} ticks)</summary>
      <div class="at-debug-scroll">
        <table>
          <thead>
            <tr>
              <th>Time</th><th>Open</th><th>High</th><th>Low</th><th>Close</th>
              {#each indicatorKeys as k}<th>{k}</th>{/each}
              <th>Signal</th><th>Decision</th>
            </tr>
          </thead>
          <tbody>
            {#each series as p (p.time)}
              <tr>
                <td>{fmtTime(p.time)}</td>
                <td class="num">{fmtNum(p.open)}</td>
                <td class="num">{fmtNum(p.high)}</td>
                <td class="num">{fmtNum(p.low)}</td>
                <td class="num">{fmtNum(p.close)}</td>
                {#each indicatorKeys as k}<td class="num">{fmtNum(p.indicators?.[k])}</td>{/each}
                <td class="at-sig at-sig-{signalLabel(p.signal).toLowerCase()}">{signalLabel(p.signal)}</td>
                <td class="num">{p.decision}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    </details>
  {/if}
</div>

<style>
  @import '../../Backtesting/Backtesting.css';
  .at-candles-toolbar { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.75rem; }
  .at-symbol-switcher { margin-bottom: 0.75rem; }
  .at-symbol-single { font-weight: 600; }
  .at-live-badge { font-size: 0.75rem; color: var(--success, #1aa260); }
  .at-debug-table { margin-top: 0.75rem; border: 1px solid var(--border-subtle, #1e1f22); border-radius: 6px; }
  .at-debug-table summary { cursor: pointer; padding: 0.5rem 0.75rem; font-size: 0.8rem; font-weight: 600; color: var(--text-secondary, #b5b6b8); }
  .at-debug-scroll { max-height: 360px; overflow: auto; }
  .at-debug-table table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
  .at-debug-table th, .at-debug-table td { padding: 0.3rem 0.5rem; border-bottom: 1px solid var(--border-subtle, #1e1f22); white-space: nowrap; }
  .at-debug-table th { position: sticky; top: 0; background: var(--surface-1, #14151a); text-align: left; }
  .at-debug-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .at-sig { font-weight: 700; }
  .at-sig-buy { color: var(--success, #1aa260); }
  .at-sig-sell { color: var(--danger, #e0533a); }
  .at-sig-hold { color: var(--text-muted, #8a8b8d); }
</style>
